import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { updateConnection } from "../../../api/connections/connectionsApi";
import { validateActionData } from "../../connections/flowMap/validateActionData";
import { ConfigTarget, actionIsUnsaved, configFor } from "./actionSetup";

type Translate = (key: any, options?: any) => string;

// Per-section editing for integration action configurations: stash the edits of
// the section the user is on, validate them client-side, PATCH only what changed.
// Every screen that renders SelectionPanel sections shares this so "Save" means
// the same thing — one action's configuration — wherever it appears.
export function useConfigEditor({ token, t, onSaved, successMessage }: {
    token: string | undefined;
    t: Translate;
    // What the toast says on a successful save — "Connection updated" inside a
    // connection, something else on a screen that isn't one.
    successMessage?: string;
    // Re-fetch the integration that was just saved, so the forms rebase on the
    // stored values instead of the ones the browser happens to hold.
    onSaved?: (targetId: string) => Promise<void> | void;
}) {
    // targetId -> actionId -> configuration payload
    const pending = useRef<Record<string, Record<string, any>>>({});
    // Latest form data per action, including the defaults rjsf populates on mount.
    // Saving a never-saved action commits these without the user having to touch a
    // field first, so accepting the defaults is one click.
    const formSnapshot = useRef<Record<string, Record<string, any>>>({});
    const [dirtyIds, setDirtyIds] = useState<Record<string, boolean>>({});
    // Bumping a target's version remounts its forms — used by Cancel (revert) and
    // Save (rebase on the stored configuration).
    const [formVersion, setFormVersion] = useState<Record<string, number>>({});
    // Field-level validation errors from the last Save attempt, in SchemaForm's
    // errors shape: targetId -> actionId -> { field: message }.
    const [formErrors, setFormErrors] = useState<Record<string, Record<string, Record<string, string>>>>({});

    const anyDirty = Object.keys(dirtyIds).some((k) => dirtyIds[k]);
    const isDirty = (targetId: string) => !!dirtyIds[targetId];

    function stashChange(target: ConfigTarget, actionId: string, data: any) {
        const existing = target.configurations?.find((c: any) => c.action?.id === actionId);
        pending.current[target.id] = pending.current[target.id] || {};
        pending.current[target.id][actionId] = existing
            ? { id: existing.id, action: actionId, data }
            : { action: actionId, data };
        setDirtyIds((prev) => (prev[target.id] ? prev : { ...prev, [target.id]: true }));
    }

    // Called on every rjsf onChange, including the mount-time default population
    // that carries no fieldId.
    function snapshotChange(target: ConfigTarget, actionId: string, data: any, fieldId?: string) {
        formSnapshot.current[target.id] = formSnapshot.current[target.id] || {};
        formSnapshot.current[target.id][actionId] = data;
        if (fieldId) stashChange(target, actionId, data);
    }

    // Seeds the pending stash from the form's current values so Save can commit an
    // action whose only content is its defaults. No-op once the user has edited it.
    function commitDefaults(target: ConfigTarget, action: any) {
        if (!action || !actionIsUnsaved(target, action)) return;
        if (pending.current[target.id]?.[action.id]) return;
        stashChange(target, action.id, formSnapshot.current[target.id]?.[action.id] ?? {});
    }

    // True when Save should be live even though nothing was edited: the action has
    // never been saved, so its defaults are still worth committing.
    const canCommitDefaults = (target: ConfigTarget | undefined, action: any) =>
        !!target && !!action && actionIsUnsaved(target, action);

    const pendingDataFor = (targetId: string, actionId: string) =>
        pending.current[targetId]?.[actionId]?.data;

    // Unsaved edits win over the saved configuration: switching sections unmounts
    // the form, and rehydrating from the stored config would show stale values
    // while Save still PATCHes the stashed edit.
    const formDataFor = (target: ConfigTarget, action: any) =>
        pendingDataFor(target.id, action.id) ?? configFor(target, action)?.data ?? {};

    async function save(target: ConfigTarget) {
        const changes = pending.current[target.id];
        if (!changes || !token) return;
        // Validate against each action's schema before PATCHing — invalid data
        // never leaves the browser.
        const targetErrors: Record<string, Record<string, string>> = {};
        Object.values(changes).forEach((cfg: any) => {
            const action = target.actions.find((a: any) => a.id === cfg.action);
            if (!action?.schema) return;
            const fieldErrors = validateActionData(action.schema, cfg.data);
            if (Object.keys(fieldErrors).length > 0) targetErrors[cfg.action] = fieldErrors;
        });
        setFormErrors((prev) => ({ ...prev, [target.id]: targetErrors }));
        if (Object.keys(targetErrors).length > 0) {
            toast.error(t("Please fix the highlighted fields before saving."));
            return;
        }
        try {
            // A 401 is swallowed by the API layer (the login modal handles it) and
            // comes back as undefined — that is not a save, so the edits stay put
            // rather than being cleared behind a "saved" toast.
            const saved = await updateConnection(token, target.id, { configurations: Object.values(changes) });
            if (!saved) return;
            toast.success(successMessage || t("Connection updated"));
            delete pending.current[target.id];
            setDirtyIds((prev) => ({ ...prev, [target.id]: false }));
            await onSaved?.(target.id);
            setFormVersion((prev) => ({ ...prev, [target.id]: (prev[target.id] || 0) + 1 }));
        } catch (e) {
            console.error(e);
            toast.error(t("Something went wrong. Please try again or contact support."));
        }
    }

    function cancel(target: ConfigTarget) {
        delete pending.current[target.id];
        setDirtyIds((prev) => ({ ...prev, [target.id]: false }));
        setFormErrors((prev) => ({ ...prev, [target.id]: {} }));
        setFormVersion((prev) => ({ ...prev, [target.id]: (prev[target.id] || 0) + 1 }));
    }

    // Confirming "Discard changes" must actually drop the stash — otherwise the
    // "discarded" edits keep re-prompting and get PATCHed by a later Save.
    function discardAll() {
        const staleIds = Object.keys(dirtyIds).filter((k) => dirtyIds[k]);
        pending.current = {};
        setDirtyIds({});
        setFormErrors({});
        setFormVersion((prev) => {
            const next = { ...prev };
            staleIds.forEach((id) => { next[id] = (next[id] || 0) + 1; });
            return next;
        });
    }

    return {
        anyDirty, isDirty, dirtyIds, formVersion, formErrors,
        stashChange, snapshotChange, commitDefaults, canCommitDefaults,
        formDataFor, save, cancel, discardAll,
    };
}
